<template>
  <div>
    <InvoiceListBase
      ref="invoiceListBase"
      v-if="invoiceTypeId"
      :key="invoiceTypeId"
      :title="invoiceTypeLabel"
      :showTypeFilter="false"
      :invoiceTypeId="invoiceTypeId"
      :invoiceTypeOptions="invoiceTypeOptions"
      :additionalHeaders="typeHeader"
      :getCurrentInvoiceCount="getCurrentInvoiceCount"
    >
      <template v-slot:type-column="{ item, isEditingMain, markMainAsChanged }">
        <td>
          <v-autocomplete
            v-if="isEditingMain(item.id)"
            v-model="item.aiInvoiceTypeId"
            :items="invoiceTypeOptions"
            item-text="name"
            item-value="id"
            dense
            hide-details
            @change="markMainAsChanged(item)"
          ></v-autocomplete>
          <span v-else>{{ item.type ? item.type.name : "-" }}</span>
        </td>
      </template>

      <template v-slot:extra-actions="{ item }">
        <v-btn
          v-if="!item.isProcessed"
          icon
          small
          color="error"
          class="ml-1"
          @click="showDeleteModal(item)"
        >
          <v-icon small>mdi-delete</v-icon>
        </v-btn>

        <v-chip
          v-if="
            invoiceTypeSlug === 'materials-services-lyunik-stroi' &&
              item.isSentToLyunikStroi
          "
          small
          color="success"
          class="ml-1"
        >
          {{ $t("LABELS.SENT") }}
        </v-chip>
        <v-btn
          v-else-if="invoiceTypeSlug === 'materials-services-lyunik-stroi'"
          icon
          small
          color="info"
          class="ml-1"
          @click="markAsSentToLyunikStroi(item)"
          :loading="markingSent === item.id"
        >
          <v-icon small>mdi-send</v-icon>
        </v-btn>

        <v-tooltip
          bottom
          v-if="
            invoiceTypeSlug === 'fuels' ||
              invoiceTypeSlug === 'materials-expenses-lyunik' ||
              invoiceTypeSlug === 'mechanization-subcontractors' ||
              invoiceTypeSlug === 'mechanization-service'
          "
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="!item.isProcessed"
              icon
              small
              color="success"
              class="ml-1"
              @click="handleExtraAction(item)"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon small>
                {{
                  invoiceTypeSlug === "fuels"
                    ? "mdi-gas-station"
                    : invoiceTypeSlug === "materials-expenses-lyunik"
                    ? "mdi-package-variant"
                    : invoiceTypeSlug === "mechanization-subcontractors"
                    ? "mdi-account-group"
                    : invoiceTypeSlug === "mechanization-service"
                    ? "mdi-file-document-edit"
                    : ""
                }}
              </v-icon>
            </v-btn>
          </template>
          <span>
            {{
              invoiceTypeSlug === "fuels"
                ? $t("LABELS.CREATE_FUEL_EXPENSE")
                : invoiceTypeSlug === "materials-expenses-lyunik"
                ? $t("LABELS.CREATE_MATERIAL_EXPENSE")
                : invoiceTypeSlug === "mechanization-subcontractors"
                ? $t("LABELS.CREATE_SUBCONTRACTOR_EXPENSE")
                : invoiceTypeSlug === "mechanization-service"
                ? $t("LABELS.CREATE_SERVICE_INVOICE")
                : ""
            }}
          </span>
        </v-tooltip>
      </template>
    </InvoiceListBase>

    <b-modal
      ref="deleteModal"
      :title="$t('LABELS.DELETE_INVOICE')"
      @ok="confirmDelete"
      ok-variant="danger"
      :ok-title="$t('BUTTON.DELETE')"
      :cancel-title="$t('LABELS.CANCEL')"
    >
      <p>{{ $t("LABELS.CONFIRM_DELETE") }}</p>
    </b-modal>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import InvoiceListBase from "../components/InvoiceListBase.vue";
import {
  FETCH_AI_INVOICE_TYPE_BY_SLUG,
  FETCH_AI_INVOICE_TYPES,
  COUNT_AI_INVOICES_BY_TYPE,
  DELETE_AI_INVOICE,
  MARK_AI_INVOICE_SENT_TO_LYUNIK_STROI
} from "@/modules/ai-invoices/store/ai-invoices.module";

export default {
  name: "AiInvoicesListByType",
  components: {
    InvoiceListBase
  },
  props: {
    invoiceTypeSlug: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      invoiceTypeId: null,
      invoiceTypeLabel: "",
      invoiceTypeOptions: [],
      typeHeader: [
        {
          text: this.$t("FORM_LABELS.TYPE"),
          value: "type",
          sortable: false
        }
      ],
      itemToDelete: null,
      markingSent: null
    };
  },
  watch: {
    invoiceTypeSlug: {
      immediate: true,
      async handler(newSlug, oldSlug) {
        if (newSlug && newSlug !== oldSlug) {
          await this.fetchInvoiceTypeBySlug();
        }
      }
    }
  },
  async mounted() {
    await this.fetchInvoiceTypeBySlug();
    await this.fetchInvoiceTypes();
    if (this.invoiceTypeId) {
      this.$store.dispatch(SET_BREADCRUMB, [
        {
          title: this.$i18n.t("MENU.ITEM.EXP.AI_INVOICES"),
          route: { name: "list-ai-invoices" }
        },
        { title: this.invoiceTypeLabel }
      ]);
    }
  },
  methods: {
    async fetchInvoiceTypeBySlug() {
      try {
        const response = await this.$store.dispatch(
          FETCH_AI_INVOICE_TYPE_BY_SLUG,
          this.invoiceTypeSlug
        );
        const type = response.data || response;
        this.invoiceTypeId = type.value || type.id;
        this.invoiceTypeLabel = type.label || type.name;

        this.$store.dispatch(SET_BREADCRUMB, [
          {
            title: this.$i18n.t("MENU.ITEM.EXP.AI_INVOICES"),
            route: { name: "list-ai-invoices" }
          },
          { title: this.invoiceTypeLabel }
        ]);
      } catch (error) {
        console.error("Error fetching invoice type:", error);
        this.$notify({
          group: "notify",
          type: "error",
          text: this.$t("LABELS.NO_RESULTS")
        });
        this.$router.push({ name: "list-ai-invoices" });
      }
    },
    async fetchInvoiceTypes() {
      try {
        const data = await this.$store.dispatch(FETCH_AI_INVOICE_TYPES);
        this.invoiceTypeOptions = data.data;
      } catch (error) {
        console.error("Error fetching invoice types:", error);
      }
    },
    async getCurrentInvoiceCount() {
      try {
        const data = await this.$store.dispatch(COUNT_AI_INVOICES_BY_TYPE, {
          slug: this.invoiceTypeSlug
        });
        return data.count || 0;
      } catch (e) {
        return 0;
      }
    },
    handleExtraAction(invoice) {
      if (this.invoiceTypeSlug === "fuels") {
        this.createFuelExpense(invoice);
      } else if (this.invoiceTypeSlug === "materials-expenses-lyunik") {
        this.createMaterialExpense(invoice);
      } else if (this.invoiceTypeSlug === "mechanization-subcontractors") {
        this.createSubcontractorExpense(invoice);
      } else if (this.invoiceTypeSlug === "mechanization-service") {
        this.createServiceInvoice(invoice);
      }
    },
    createFuelExpense(invoice) {
      this.$router.push({
        name: "add-fuel-expenses-from-ai",
        params: { aiInvoiceId: invoice.id }
      });
    },
    createMaterialExpense(invoice) {
      this.$router.push({
        name: "add-subcontractor-expenses-materials-from-ai",
        params: { aiInvoiceId: invoice.id }
      });
    },
    createSubcontractorExpense(invoice) {
      this.$router.push({
        name: "add-subcontractor-expenses-from-ai",
        params: { aiInvoiceId: invoice.id }
      });
    },
    createServiceInvoice(invoice) {
      this.$router.push({
        name: "add-service-invoices-from-ai",
        params: { aiInvoiceId: invoice.id }
      });
    },
    showDeleteModal(item) {
      this.itemToDelete = item;
      this.$refs.deleteModal.show();
    },
    async confirmDelete() {
      if (!this.itemToDelete) return;
      try {
        await this.$store.dispatch(DELETE_AI_INVOICE, this.itemToDelete.id);
        this.$notify({
          group: "notify",
          type: "success",
          text: this.$t("ALERTS.SUCCESS")
        });
        this.itemToDelete = null;
        this.$refs.invoiceListBase.getDataFromApi();
      } catch (error) {
        this.$notify({
          group: "notify",
          type: "error",
          text: this.$t("ALERTS.ERROR")
        });
      }
    },
    async markAsSentToLyunikStroi(item) {
      this.markingSent = item.id;
      try {
        await this.$store.dispatch(
          MARK_AI_INVOICE_SENT_TO_LYUNIK_STROI,
          item.id
        );
        this.$notify({
          group: "notify",
          type: "success",
          text: this.$t("LABELS.SENT_SUCCESS_TO_LYUNIK_STROI")
        });
        this.$refs.invoiceListBase.getDataFromApi();
      } catch (error) {
        this.$notify({
          group: "notify",
          type: "error",
          text: this.$t("ALERTS.ERROR")
        });
      } finally {
        this.markingSent = null;
      }
    }
  }
};
</script>
